import { AxiosInstance } from 'axios';
import {
  positionOpenerAxiosApi,
  resolveOpenerAxios
} from 'src/Config/axiosApi';

/**
 * Per-group opener rule activation — base-URL routing.
 *
 * The opener execution plane is sharded per opener-group: a grouped rule must
 * be activated/paused/toggled against its OWNING group's feed pod, not the
 * always-on core `position-opener-node`. The core pod 409s any write on a
 * grouped pair (so the FE MUST route grouped writes to the group endpoint),
 * and a feed pod 404s another group's rule. Ungrouped pairs use the core base.
 *
 * FAIL VISIBLE (spec D4/O5): a grouped rule whose feed base can't be resolved
 * (the `VITE_OPENER_FEED_URL` env is missing) does NOT silently fall back to
 * the core pod — that is exactly how a grouped "off" write landed on core,
 * got served with a 200, and re-armed the rule. Instead we throw
 * `UnresolvedOpenerGroupError`; the mutation's `onError` (and the app-wide
 * `MutationCache.onError` backstop) surfaces it so the operator KNOWS the
 * write didn't route. Only a genuinely ungrouped rule uses core.
 *
 * Endpoint shapes:
 *   grouped:   `${VITE_OPENER_FEED_URL}/groups/{openerGroupId}/api/position-opener`
 *   ungrouped: `${VITE_POSITION_OPENER_URL}/api/position-opener`  (core base)
 */

/**
 * Thrown when a write targets a GROUPED rule but its feed pod base URL can't
 * be resolved (no `VITE_OPENER_FEED_URL`). Never swallowed — it propagates to
 * the mutation `onError` so the failed routing is visible, never degraded to a
 * silent core write.
 */
export class UnresolvedOpenerGroupError extends Error {
  constructor(public readonly openerGroupId: string) {
    super(
      `Cannot route opener write for group ${openerGroupId}: ` +
        'VITE_OPENER_FEED_URL is not configured. The write was NOT sent.'
    );
    this.name = 'UnresolvedOpenerGroupError';
  }
}

/** The core (ungrouped) opener API base, including the `/api/position-opener`
 * path. Mirrors the `positionOpenerAxiosApi` baseURL so reads/bulk/ungrouped
 * writes resolve to the same instance. */
export function coreOpenerBase(): string {
  return import.meta.env.VITE_POSITION_OPENER_URL + '/api/position-opener';
}

/**
 * Resolve the opener API base URL for a rule, given its `openerGroupId`.
 * - Grouped (non-empty id) + feed base env present → the group's feed pod.
 * - Grouped + feed base env MISSING → throws `UnresolvedOpenerGroupError`
 *   (fail visible — never a silent core fall-back, see D4 above).
 * - Ungrouped (null/empty id) → the core base.
 */
export function resolveOpenerBase(openerGroupId?: string | null): string {
  if (openerGroupId) {
    const feedBase = import.meta.env.VITE_OPENER_FEED_URL as string | undefined;
    if (!feedBase) throw new UnresolvedOpenerGroupError(openerGroupId);
    return `${feedBase}/groups/${openerGroupId}/api/position-opener`;
  }
  return coreOpenerBase();
}

/**
 * The axios instance to issue an opener write against, given the rule's
 * `openerGroupId`. The instance carries the same bearer-token + 401-refresh
 * interceptors as the core `positionOpenerAxiosApi` (per-base instances are
 * cached in `axiosApi.ts`), so a grouped activation routed to a feed pod is
 * still authenticated and survives a silent token refresh. The core base
 * resolves back to `positionOpenerAxiosApi` unchanged.
 */
export function resolveOpenerApi(openerGroupId?: string | null): AxiosInstance {
  return resolveOpenerAxios(resolveOpenerBase(openerGroupId));
}

/**
 * Look up a rule's owning `openerGroupId` from the cached manual-confirmed
 * pairs list that `useManualConfirmedPairs` loads (each entry's `_id` is the
 * pairId and `adaptPair` already maps `openerGroupId`). A rule references its
 * pair via `pairId`; pass the rule's `pairId` here. Returns `null` when the
 * pair isn't found or has no group (→ core routing).
 */
export function openerGroupIdForPair(
  pairId: string | null | undefined,
  pairs: ReadonlyArray<{ _id: string; openerGroupId?: string | null }> | undefined
): string | null {
  if (!pairId || !pairs) return null;
  const pair = pairs.find(p => p._id === pairId);
  return pair?.openerGroupId ?? null;
}

export { positionOpenerAxiosApi };
